import { Injectable } from '@angular/core';
import { ArchiveState, SurveyUserTypes } from '../constants-types/Enums';
import {
    BulkUpdateType,
    BulkUpdateEntry
} from '../core-data-model/BulkUpdateApi.connectinterface';
import {
    BulkUpdatePost,
    BulkUpdateTeamPost,
    BulkUpdatePersonPost,
    BulkUpdatePeopleAndJobPost,
    BulkUpdatePersonPostPersonsAction
} from '../core-data-model/BulkUpdateApi.serverinterface';
import { Person } from '../core-data-model/Person.model';
import { SearchResult } from '../core-data-model/SearchResult.model';

export type PersonJobUpdate = {
    personId: string;
    jobId: string;
};

@Injectable({
    providedIn: 'root'
})
export class BulkUpdateTransformers {
    private getBatchActionFromUpdateType(updateType: BulkUpdateType): string {
        let batchAction: string;
        switch (updateType) {
            case BulkUpdateType.MoveToFolder:
            case BulkUpdateType.UpdateJob:
            case BulkUpdateType.Archive:
            case BulkUpdateType.UpdateType:
            case BulkUpdateType.RemoveAssociatedJob:
                batchAction = 'Update';
                break;
            case BulkUpdateType.AddToTeam:
                batchAction = 'Create';
                break;
            case BulkUpdateType.RemoveFromTeam:
                batchAction = 'Delete';
                break;
            // 'Add' and 'Move' are unused currently.
        }
        return batchAction;
    }

    private transformBulkUpdateTeam(
        batchAction: string,
        destinationId: string,
        updateEntry: BulkUpdateEntry
    ): BulkUpdateTeamPost {
        const teamItemAction = {
            id: updateEntry.id,
            teamId: destinationId,
            isPerson: updateEntry.isPerson
        };
        return {
            batchAction,
            teamItemAction
        };
    }

    private transformBulkUpdatePerson(
        batchAction: string,
        destinationId: string,
        updateType: BulkUpdateType,
        updateEntry: BulkUpdateEntry
    ): BulkUpdatePersonPost {
        const personsAction: BulkUpdatePersonPostPersonsAction = {
            id: updateEntry.id
        };
        if (updateType === BulkUpdateType.UpdateJob) {
            Object.assign(personsAction, {
                jobId: destinationId
            });
        } else if (updateType === BulkUpdateType.UpdateType) {
            Object.assign(personsAction, {
                // Need to send key name for enum
                personType: SurveyUserTypes[destinationId as any]
            });
        } else if (updateType === BulkUpdateType.RemoveAssociatedJob) {
            Object.assign(personsAction, {
                removeJobAssociation: true
            });
        }
        return {
            personsAction,
            batchAction
        };
    }

    private transformBulkUpdatePeopleAndJob(
        batchAction: string,
        destinationId: string,
        updateType: BulkUpdateType,
        updateEntry: BulkUpdateEntry
    ): BulkUpdatePeopleAndJobPost {
        let destination;
        switch (updateType) {
            case BulkUpdateType.AddToTeam:
                destination = {
                    addToMyTeamId: destinationId
                };
                break;
            case BulkUpdateType.MoveToFolder:
                destination = {
                    moveToFolderId: destinationId
                };
                break;
            case BulkUpdateType.Archive:
                destination = {
                    isArchived: ArchiveState.Archived
                };
                break;
            default:
                destination = {};
        }
        return {
            batchAction,
            peopleAndJobAction: Object.assign(destination, {
                id: updateEntry.id,
                isPerson: updateEntry.isPerson
            })
        };
    }

    transformBulkUpdatePost(
        updateType: BulkUpdateType,
        updateEntries: BulkUpdateEntry[],
        destinationId: string
    ): BulkUpdatePost {
        const batchAction = this.getBatchActionFromUpdateType(updateType);

        if (
            updateType === BulkUpdateType.UpdateJob ||
            updateType === BulkUpdateType.UpdateType ||
            updateType === BulkUpdateType.RemoveAssociatedJob
        ) {
            return updateEntries.map((updateEntry) =>
                this.transformBulkUpdatePerson(
                    batchAction,
                    destinationId,
                    updateType,
                    updateEntry
                )
            );
        }

        if (
            updateType === BulkUpdateType.RemoveFromTeam ||
            updateType === BulkUpdateType.AddToTeam
        ) {
            return updateEntries.map((updateEntry) =>
                this.transformBulkUpdateTeam(
                    batchAction,
                    destinationId,
                    updateEntry
                )
            );
        }

        return updateEntries.map((updateEntry) =>
            this.transformBulkUpdatePeopleAndJob(
                batchAction,
                destinationId,
                updateType,
                updateEntry
            )
        );
    }

    transformBulkUpdatePersonJob(
        updateType: BulkUpdateType,
        updateEntries: PersonJobUpdate[]
    ) {
        const batchAction = this.getBatchActionFromUpdateType(updateType);
        return updateEntries.map((u) =>
            this.transformBulkUpdatePerson(batchAction, u.jobId, updateType, {
                id: u.personId,
                isPerson: true
            })
        );
    }

    transformBulkUpdateEntries(rows: SearchResult[]): BulkUpdateEntry[] {
        return rows.map((row) => ({
            id: row.id,
            isPerson: row instanceof Person
        }));
    }
}
